import { Box, Link, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import React, { useCallback, useRef, useState } from 'react';
import useLazyload from '../../../../hooks/useLazyload';
import { ISeason } from '../../../../services/types';
import CurationInfo from './CurationInfo';

const useStyles = makeStyles((theme: Theme) => ({
  coverUrl: {
    objectFit: 'cover',
    objectPosition: 'center',
    borderRadius: 5,
    marginBottom: 6,
    width: '285px',
    height: '162px',
  },
  label: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    position: 'absolute',
    left: 0,
    bottom: 6,
    backgroundColor: '#000',
    color: '#fff',
    padding: '0 8px',
    fontFamily: 'Georgia, serif',
    fontWeight: 'bold',
    fontSize: 12,
  },
  title: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    fontSize: 17,
    fontWeight: 'bold',
  },
  subTitle: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    fontSize: 14,
    color: '#333',
  },
  actionText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.secondary.main,
    textTransform: 'capitalize',
  },
  container: {
    padding: theme.spacing(0.5),
    width: 294,
    overflow: 'hidden',
    position: 'relative',
    zIndex: 1,
  },
}));

interface IProps {
  title?: string;
  subTitle?: string;
  actionText?: string;
  label?: string;
  type?: string;
  object: { id: number; trackingId?: string; url?: string };
  curation: { id: number; segmentId: number; priority: number };
}

const DefaultImage = 'https://cdn.radishfiction.com/featured/collection_default_banner.png';
const regex = /(http[s]?:\/\/|data:image)/;
const getCoverUrl = (type = '', object: any) => {
  if (type === 'story') {
    return object?.story.seasons[0].additionalCoverHUrl || object?.story.seasons[0].coverUrl;
  } else if (type === 'season') {
    const season = object?.story?.seasons.find((s: ISeason) => Number(s.id) === Number(object.id));
    return season ? season.additionalCoverHUrl || season.coverUrl : DefaultImage;
  }

  return object?.coverUrl || object?.additionalCoverHUrl || DefaultImage;
};

const PreviewFeaturedCard = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const { title, subTitle, actionText, label, type, object } = props;
  const src = getCoverUrl(type, object);

  const imgRef = useRef<HTMLImageElement | null>();
  const [isShowImage, setIsShowImage] = useState<boolean>(false);

  const getTargetUrl = () => {
    switch (type) {
      case 'story':
        return `${process.env.CREW_DEFAULT_URI}/stories/${object.id}`;
      case 'collection':
        return `/home/collection/v/${object.trackingId}`;
      case 'link':
        return `/home/link/v/${object.id}`;
    }
  };

  const imgSrc = regex.test(src) ? `${src}?w=320` : `${process.env.CDN_URL}/${src}?w=320`;
  const handleImageLoad = useCallback(() => setIsShowImage(true), []);

  useLazyload(imgRef, handleImageLoad);

  return (
    <Box className={classes.container}>
      <CurationInfo location="top" segmentId={props.curation.segmentId} priority={props.curation.priority} />
      <Link href={getTargetUrl()} target="_blank">
        <Typography className={classes.title}>{title}</Typography>
        <Typography className={classes.subTitle}>{subTitle}</Typography>
        <Typography className={classes.actionText}>{actionText}</Typography>
        <Box position="relative">
          <img src={isShowImage ? imgSrc : DefaultImage} className={classes.coverUrl} alt={title} ref={ref => (imgRef.current = ref)} />
          <Typography className={classes.label}>{label}</Typography>
        </Box>
      </Link>
    </Box>
  );
};

export default PreviewFeaturedCard;
